import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { Plus, Trash2, Edit, ExternalLink, Download, Upload, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthenticatedAxios, handleAuthError } from '../utils/api';

// Компонент для отображения картинки токена
const TokenImage = ({ address, symbol }) => {
  const [iconUrl, setIconUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchIcon = async () => {
      try {
        const api = getAuthenticatedAxios();
        const response = await api.get(`/api/wallets/jetton-icon/${address}`);
        if (response.data.success && response.data.icon_url) {
          setIconUrl(response.data.icon_url);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchIcon();
  }, [address]);

  if (loading) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
    );
  }

  if (error || !iconUrl) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
        {symbol?.slice(0, 2)?.toUpperCase() || <Coins className="w-4 h-4" />}
      </div>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={symbol}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setError(true)}
    />
  );
};

// URL для просмотра токена на TONScan
const getTonScanUrl = (address) => `https://tonscan.org/address/${address}`;
const getTonViewerUrl = (address) => `https://tonviewer.com/${address}`;

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [newToken, setNewToken] = useState({
    token_symbol: '',
    token_address: '',
    enabled: true,
    min_swap_amount_ton: 0.1,
    max_swap_amount_ton: 1.0,
    priority: 1,
  });

  useEffect(() => {
    fetchTokens();
  }, []);

  // Экспорт токенов в JSON файл
  const handleExport = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/warmup/tokens/export');
      if (response.data.success) {
        const dataStr = JSON.stringify(response.data.tokens, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `warmup-tokens-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Экспортировано ${response.data.count} токенов`);
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Ошибка экспорта');
      }
    }
  };

  // Импорт токенов из JSON файла
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const importedTokens = JSON.parse(text);
      
      if (!Array.isArray(importedTokens) || importedTokens.length === 0) {
        toast.error('Неверный формат файла');
        return;
      }

      const replaceExisting = window.confirm(
        `Найдено ${importedTokens.length} токенов.\n\nЗаменить все существующие токены? (Да - заменить, Нет - добавить к существующим)`
      );

      const api = getAuthenticatedAxios();
      const response = await api.post('/api/warmup/tokens/import', {
        tokens: importedTokens,
        replace_existing: replaceExisting
      });

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchTokens();
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error(`Ошибка импорта: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fetchTokens = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/warmup/tokens');
      if (response.data.success) {
        setTokens(response.data.tokens);
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to fetch tokens');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToken = async () => {
    try {
      const api = getAuthenticatedAxios();
      await api.post('/api/warmup/tokens', newToken);
      toast.success('Token added');
      setShowAddDialog(false);
      setNewToken({
        token_symbol: '',
        token_address: '',
        enabled: true,
        min_swap_amount_ton: 0.1,
        max_swap_amount_ton: 1.0,
        priority: 1,
      });
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to add token');
      }
    }
  };

  const handleDeleteToken = async (id) => {
    if (!window.confirm('Are you sure you want to delete this token?')) {
      return;
    }
    try {
      const api = getAuthenticatedAxios();
      await api.delete(`/api/warmup/tokens/${id}`);
      toast.success('Token deleted');
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to delete token');
      }
    }
  };

  const handleToggleEnabled = async (id, enabled) => {
    try {
      const api = getAuthenticatedAxios();
      const token = tokens.find(t => t.id === id);
      await api.put(`/api/warmup/tokens/${id}`, {
        ...token,
        enabled: !enabled,
      });
      toast.success('Token updated');
      fetchTokens();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error('Failed to update token');
      }
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Token Management</CardTitle>
            <CardDescription>Управление токенами для warmup операций</CardDescription>
          </div>
          <div className="flex gap-2">
            {/* Скрытый input для импорта */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImport}
              accept=".json"
              className="hidden"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <Upload className="h-4 w-4 mr-2" />
              {importing ? 'Импорт...' : 'Импорт'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExport}
              disabled={tokens.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Экспорт
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {tokens.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Coins className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Нет добавленных токенов</p>
            <p className="text-sm">Добавьте токен или импортируйте список</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Токен</TableHead>
                <TableHead>Адрес</TableHead>
                <TableHead>Мин. (TON)</TableHead>
                <TableHead>Макс. (TON)</TableHead>
                <TableHead>Приоритет</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.id}>
                  <TableCell>
                    <TokenImage address={token.token_address} symbol={token.token_symbol} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{token.token_symbol}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-gray-600">
                        {token.token_address.slice(0, 8)}...{token.token_address.slice(-6)}
                      </span>
                      <div className="flex gap-1">
                        <a
                          href={getTonScanUrl(token.token_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="Открыть на TONScan"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <a
                          href={getTonViewerUrl(token.token_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-500 hover:text-purple-700 transition-colors"
                          title="Открыть на TONViewer"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{token.min_swap_amount_ton}</TableCell>
                  <TableCell>{token.max_swap_amount_ton}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{token.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={token.enabled}
                      onCheckedChange={() => handleToggleEnabled(token.id, token.enabled)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteToken(token.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {showAddDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add Token</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Token Symbol</Label>
                  <Input
                    value={newToken.token_symbol}
                    onChange={(e) => setNewToken({ ...newToken, token_symbol: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Token Address</Label>
                  <Input
                    value={newToken.token_address}
                    onChange={(e) => setNewToken({ ...newToken, token_address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Amount (TON)</Label>
                    <Input
                      type="number"
                      value={newToken.min_swap_amount_ton}
                      onChange={(e) => setNewToken({ ...newToken, min_swap_amount_ton: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Max Amount (TON)</Label>
                    <Input
                      type="number"
                      value={newToken.max_swap_amount_ton}
                      onChange={(e) => setNewToken({ ...newToken, max_swap_amount_ton: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Priority (1-10, lower is higher priority)</Label>
                  <Input
                    type="number"
                    value={newToken.priority}
                    onChange={(e) => setNewToken({ ...newToken, priority: parseInt(e.target.value) })}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddToken}>Add</Button>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TokenManagement;

